/**
 * useTenant Hook
 * 
 * Core tenant context hook that provides access to current tenant data, global admin status,
 * and tenant-related helper functions throughout the application. Wraps TenantContext and
 * exposes tenant object, isGlobalAdmin flag, tenant branding, and utility functions.
 * 
 * This is the primary interface for all components and hooks requiring tenant awareness,
 * replacing direct context consumption with a clean hook API.
 * 
 * Pattern based on RoleViewContext/useRoleView for consistency across context providers.
 */

import { useCallback, useMemo } from 'react';
import { useTenantContext } from '@/context/TenantContext';
import { useRoleView } from '@/context/RoleViewContext';
import type { Tenant } from '@/lib/tenantDetection';

/**
 * Extended tenant hook return type with helper functions
 */
export interface UseTenantResult {
 // Core tenant data (all properties from Tenant interface)
 tenant: Tenant | null;
 
 // Loading and error states
 isLoading: boolean;
 isGlobalAdmin: boolean;
 error: string | null;
 
 // Tenant management functions
 refreshTenant: (options?: { skipPersist?: boolean }) => Promise<void>;
 switchTenant: (tenantId: string | null) => Promise<void>;
 
 // Helper functions for tenant-related checks
 hasTenantAccess: (tenantId: string) => boolean;
 isCurrentTenant: (tenantId: string) => boolean;
 hasVertical: (vertical: string) => boolean;
 
 /**
 * Whether data queries should filter by tenant.
 * True when user is NOT a global admin, OR when global admin is viewing as a non-global-admin role.
 * USE THIS FOR: Adding tenant filtering to data queries.
 */
 shouldFilterByTenant: boolean;
}

/**
 * useTenant Hook
 * 
 * Primary hook for accessing tenant context throughout the application.
 * Provides tenant data, loading states, management functions, and helper utilities.
 * 
 * @returns UseTenantResult with tenant data and helper functions
 * 
 * @example
 * ```tsx
 * const { tenant, isGlobalAdmin, hasVertical, hasTenantAccess } = useTenant();
 * 
 * // Check if current tenant has specific vertical enabled
 * if (hasVertical('pest_control')) {
 * // Show pest control content
 * }
 * 
 * // Check if global admin can access specific tenant
 * if (hasTenantAccess(targetTenantId)) {
 * // Allow tenant management
 * }
 * 
 * // Display tenant branding
 * <img src={tenant?.logo_url} alt={tenant?.name} />
 * ```
 */
export function useTenant(): UseTenantResult {
 // Access base tenant context
 // useTenantContext handles error boundaries and returns safe fallbacks
 const {
 tenant,
 isLoading,
 isGlobalAdmin,
 error,
 switchTenant,
 refreshTenant,
 } = useTenantContext();
 
 // Access role view context for determining effective permissions
 const { viewingAsRole } = useRoleView();
 
 /**
 * Determine if data queries should filter by tenant.
 * True when:
 * - User is NOT a global admin (always filter), OR
 * - User IS a global admin but viewing as a non-global-admin role
 */
 const shouldFilterByTenant = useMemo(() => {
 if (!isGlobalAdmin) {
 return true; // Non-global admins always filter by tenant
 }
 // Global admins filter by tenant when viewing as a non-global-admin role
 return viewingAsRole !== null && viewingAsRole !== 'global_admin';
 }, [isGlobalAdmin, viewingAsRole]);

 /**
 * Check if current user has access to a specific tenant
 * 
 * Returns true if:
 * - User is a global_admin (can access all tenants)
 * - tenantId matches current tenant (user has access to their own tenant)
 * 
 * @param tenantId - Tenant ID to check access for
 * @returns true if user can access the tenant, false otherwise
 */
 const hasTenantAccess = useCallback((tenantId: string): boolean => {
 // Global admins can access all tenants
 if (isGlobalAdmin) {
 return true;
 }
 
 // Non-global-admins can only access their own tenant
 return tenant?.id === tenantId;
 }, [isGlobalAdmin, tenant?.id]);

 /**
 * Check if given tenant ID matches the current tenant
 * 
 * Useful for conditional rendering and active state indicators.
 * 
 * @param tenantId - Tenant ID to compare against current tenant
 * @returns true if tenantId matches current tenant, false otherwise
 */
 const isCurrentTenant = useCallback((tenantId: string): boolean => {
 return tenant?.id === tenantId;
 }, [tenant?.id]);

 /**
 * Check if current tenant has a specific vertical enabled
 * 
 * Verticals determine which business content (training, templates) is visible.
 * Available verticals: pest_control, fiber_internet, life_insurance, solar, alarms, home_improvement
 * 
 * @param vertical - Vertical identifier to check
 * @returns true if tenant has the vertical enabled, false otherwise
 */
 const hasVertical = useCallback((vertical: string): boolean => {
 if (!tenant) {
 return false;
 }
 
 // Check if vertical exists in tenant's enabled_verticals array
 return tenant.enabled_verticals.includes(vertical);
 }, [tenant]);

 // Return comprehensive tenant context with helper functions
 return {
 // Core tenant data (pass-through from context)
 tenant,
 isLoading,
 isGlobalAdmin,
 error,
 
 // Management functions (pass-through from context)
 refreshTenant,
 switchTenant,
 
 // Helper functions (enhanced with useCallback for performance)
 hasTenantAccess,
 isCurrentTenant,
 hasVertical,
 
 // Effective permissions helper
 shouldFilterByTenant,
 };
}
